/**
 * Vault y sobres de item.
 *
 * Cada vault (personal u organización) tiene una clave simétrica aleatoria
 * (`vaultKey`). La de un vault personal se envuelve con la clave de cuenta; la
 * de un vault de organización se reparte por miembro (ver `org.ts`).
 *
 * Cada item se guarda como un SOBRE AES-256-GCM cuyo AAD ata el criptograma a
 * su identidad: `boveda:item:v1:{vaultId}:{itemId}:{rev}`. Así el servidor no
 * puede mover un sobre a otro item/vault ni revertir una revisión sin que el
 * open falle.
 */

import { base64ToBytes, bytesToBase64, utf8Decode, utf8Encode } from "./encoding";
import { openBase64, randomSymmetricKey, sealBase64 } from "./primitives";
import type { ProtectedItem, VaultItemPlain } from "./types";

const AAD_PERSONAL_VAULT_KEY = "boveda:vault-key:v1";
const AAD_VAULT_NAME_PREFIX = "boveda:vault-name:v1:";

export function itemAad(vaultId: string, itemId: string, rev: number): string {
  return `boveda:item:v1:${vaultId}:${itemId}:${rev}`;
}

/**
 * Cifra el NOMBRE de un vault a un sobre opaco (`name_envelope`). El AAD ata el
 * sobre a su vault: el servidor no puede reasignar un nombre a otro vault sin
 * que el open falle. Se usa en vaults de organización (el nombre del equipo es
 * metadato sensible); el vault personal usa el literal "Personal".
 */
export async function encryptVaultName(
  vaultKey: Uint8Array,
  vaultId: string,
  name: string,
): Promise<string> {
  return sealBase64(vaultKey, utf8Encode(name), AAD_VAULT_NAME_PREFIX + vaultId);
}

export async function decryptVaultName(
  vaultKey: Uint8Array,
  vaultId: string,
  envelope: string,
): Promise<string> {
  return utf8Decode(await openBase64(vaultKey, envelope, AAD_VAULT_NAME_PREFIX + vaultId));
}

/** Crea la clave de un vault nuevo (32 bytes aleatorios). */
export function createVaultKey(): Uint8Array {
  return randomSymmetricKey();
}

/** Envuelve la clave de un vault PERSONAL con la clave de cuenta. */
export async function wrapPersonalVaultKey(
  accountKey: Uint8Array,
  vaultKey: Uint8Array,
): Promise<string> {
  return sealBase64(accountKey, vaultKey, AAD_PERSONAL_VAULT_KEY);
}

export async function unwrapPersonalVaultKey(
  accountKey: Uint8Array,
  wrapped: string,
): Promise<Uint8Array> {
  return openBase64(accountKey, wrapped, AAD_PERSONAL_VAULT_KEY);
}

/** Cifra un item en claro a un `ProtectedItem` (blob opaco para el servidor). */
export async function encryptItem(
  vaultKey: Uint8Array,
  vaultId: string,
  itemId: string,
  rev: number,
  plain: VaultItemPlain,
): Promise<ProtectedItem> {
  const bytes = utf8Encode(JSON.stringify(plain));
  const envelope = await sealBase64(vaultKey, bytes, itemAad(vaultId, itemId, rev));
  return { id: itemId, vaultId, envelope, rev };
}

export async function decryptItem(
  vaultKey: Uint8Array,
  item: ProtectedItem,
): Promise<VaultItemPlain> {
  const bytes = await openBase64(vaultKey, item.envelope, itemAad(item.vaultId, item.id, item.rev));
  // Trust boundary: el sobre lo selló ESTE cliente (AEAD ya autenticó el contenido).
  // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion
  return JSON.parse(utf8Decode(bytes)) as VaultItemPlain;
}

/** Re-cifra un item bajo una nueva clave de vault (rotación) conservando id. */
export async function reencryptItem(
  oldVaultKey: Uint8Array,
  newVaultKey: Uint8Array,
  item: ProtectedItem,
): Promise<ProtectedItem> {
  const plain = await decryptItem(oldVaultKey, item);
  return encryptItem(newVaultKey, item.vaultId, item.id, item.rev + 1, plain);
}

export { base64ToBytes, bytesToBase64 };
